import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FileText, CheckCircle, XCircle } from 'lucide-react';
import { AdminPageHeader, AdminPageShell, AdminPageBody } from '../../components/AdminPageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const formatDT = (v) => v ? new Date(v).toLocaleString('pt-BR') : '-';

const STATUS_LABEL = {
  sent: { label: 'Documentação enviada', cls: 'bg-blue-100 text-blue-800' },
  approved: { label: 'Aprovado', cls: 'bg-emerald-100 text-emerald-800' },
  denied: { label: 'Recusado', cls: 'bg-red-100 text-red-800' },
  pending: { label: 'Pendente de análise', cls: 'bg-amber-100 text-amber-800' },
  not_applicable: { label: 'Não aplicável', cls: 'bg-gray-100 text-gray-700' },
};

const AdminMinorDocs = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) { navigate('/admin/login'); return; }
    load();
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/minors/pending-review`);
      const docs = (r.data?.documents || []).map(d => ({
        ...d,
        package_name: d.reservation_info?.package_name,
        selected_date: d.reservation_info?.selected_date,
        customer_name: d.traveler_name || d.reservation_info?.owner_name,
      }));
      setItems(docs);
    } catch (e) { setMsg({ type: 'error', text: 'Erro ao carregar.' }); }
    finally { setLoading(false); }
  };

  const decide = async (doc, approve) => {
    const reason = approve ? null : window.prompt('Motivo da recusa:');
    if (!approve && !reason) return;
    try {
      await axios.post(`${API_URL}/api/minors/decide-document`, { document_id: doc.id, approve, reason });
      setMsg({ type: 'success', text: `Documento ${approve ? 'aprovado' : 'recusado'}.` });
      load();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Erro.' }); }
  };

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Documentos de Menores"
        description="Fila de análise dos documentos enviados pelos titulares"
        Icon={FileText}
      />
      <AdminPageBody>
        {msg && <div className={`p-3 rounded ${msg.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{msg.text}</div>}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">Documentos ({items.length})</h3>
              <Button variant="outline" size="sm" onClick={load}>Atualizar</Button>
            </div>
            {loading ? <p className="text-sm text-gray-500">Carregando…</p> :
              items.length === 0 ? <p className="text-sm text-gray-500 text-center py-8" data-testid="empty-docs">Nenhum documento pendente.</p> :
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left">Cliente/Reserva</th>
                      <th className="p-2 text-left">Tipo</th>
                      <th className="p-2 text-left">Arquivo</th>
                      <th className="p-2 text-left">Enviado</th>
                      <th className="p-2 text-center">Status</th>
                      <th className="p-2 text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(d => {
                      const st = STATUS_LABEL[d.status] || STATUS_LABEL.pending;
                      return (
                        <tr key={d.id} className="border-b hover:bg-gray-50" data-testid={`doc-row-${d.id}`}>
                          <td className="p-2">
                            <div className="font-medium">{d.customer_name}</div>
                            <div className="text-xs text-gray-500">{d.package_name} · {d.selected_date}</div>
                          </td>
                          <td className="p-2 text-xs">{d.document_type}</td>
                          <td className="p-2 text-xs">{d.filename}</td>
                          <td className="p-2 text-xs">{formatDT(d.uploaded_at)}</td>
                          <td className="p-2 text-center"><Badge className={st.cls}>{st.label}</Badge></td>
                          <td className="p-2 text-center">
                            {d.status === 'sent' && (
                              <div className="flex gap-1 justify-center">
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${d.id}`} onClick={() => decide(d, true)}>
                                  <CheckCircle className="w-3 h-3 mr-1" /> Aprovar
                                </Button>
                                <Button size="sm" variant="outline" className="border-red-500 text-red-600" data-testid={`deny-${d.id}`} onClick={() => decide(d, false)}>
                                  <XCircle className="w-3 h-3 mr-1" /> Recusar
                                </Button>
                              </div>
                            )}
                            {d.status === 'denied' && d.decision_reason && (
                              <span className="text-xs text-red-700">Motivo: {d.decision_reason}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            }
          </CardContent>
        </Card>
      </AdminPageBody>
    </AdminPageShell>
  );
};

export default AdminMinorDocs;
